import { usePageContext } from '@/components/page-context/context'
import type { ButtonPageAction } from '@/components/page-context/types'
import { type AccessControlInput, useAccessControl } from '@/hooks/access-control'
import { useRouter } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { type PendingPageAction, usePendingPageActionStore } from './pendingPageActionStore'

const NO_ACCESS: AccessControlInput = {}

/**
 * Runs a requested header action once its page shows it. Pages register their header actions from
 * their own effects after loading, so this waits on that registration, an outside event, rather than
 * on anything the palette could call directly.
 */
export function usePendingPageActionRunner() {
	const router = useRouter()
	const { actions, actionsPathname } = usePageContext()
	const pending = usePendingPageActionStore(s => s.pending)
	const clear = usePendingPageActionStore(s => s.clear)
	const action =
		pending && actionsPathname === pending.pathname
			? actions.find((entry): entry is ButtonPageAction => entry.type !== 'component' && entry.id === pending.actionId)
			: undefined
	// The page wraps its button in this gate; calling the handler directly has to apply it too.
	const access = useAccessControl(action?.access ?? NO_ACCESS)
	const isGatedOff = access.state === 'denied' || access.state === 'blocked'

	// The request is made before its own navigation starts, so any navigation to another path means the user left.
	useEffect(
		() =>
			router.subscribe('onBeforeNavigate', ({ toLocation }) => {
				const current = usePendingPageActionStore.getState().pending
				if (current && toLocation.pathname !== current.pathname) {
					clear()
				}
			}),
		[router, clear],
	)

	// A request is marked as run rather than cleared, so this effect never writes state.
	const handledRef = useRef<PendingPageAction | null>(null)
	useEffect(() => {
		if (!pending || !action || handledRef.current === pending || Date.now() > pending.expiresAt) {
			return
		}
		handledRef.current = pending
		const handler = pending.childId ? action.children?.find(child => child.id === pending.childId) : action
		if (handler && !handler.disabled && !action.disabled && !action.loading && !isGatedOff) {
			handler.onClick()
		}
	}, [action, isGatedOff, pending])
}
